import { 
    useState, 
    useLayoutEffect,
    useEffect,
    useReducer,
    useMemo,
    useRef,
    useCallback,
    forwardRef,
    StrictMode,
    createContext,
    useContext
} from 'react';


export function getAddressFromComponents(addressComponents = [], location = null, options = {}) {
    const { 
        regionTypes = [], 
        cityTypes = [],
        debug = false
    } = options

    const newAddress = {
        country: '',
        postal_code: '',
        street: '',
        region: '',
        region_code: '',
        city: '',
        latLng: {
            lat: 0,
            lng: 0
        }
    }

    if ( !addressComponents.length )
        return newAddress;

    const template = {
        country: [
            'country'
        ],
        postal_code: [
            'postal_code'
        ],
        region: regionTypes,
        region_code: regionTypes,
        city: cityTypes,
        street: [
            'street_address', 
            'route'
        ],
    }

    let debugMessage = ''

    /**
     * newAddress - address to state (postal_code|region|city|street|latLng)
     * addressComponents = address compotents from google place
     * template - address types template
     */
    for ( const key in newAddress ) {
        if ( newAddress.hasOwnProperty( key ) && template.hasOwnProperty( key ) ) {
            const nameType = key.includes( '_code' ) ? 'short_name' : 'long_name'
            const types = template[key]

            debugMessage += `${key}: `
            
            types.forEach( type => {
                const targetComponent = addressComponents.find( c => c.types.includes( type ) )

                if ( !!targetComponent?.[nameType] && !newAddress[key] ) {
                    newAddress[key] = targetComponent[nameType]
                }

                if ( !!targetComponent?.[nameType] ) {
                    debugMessage += `"${targetComponent[nameType]}" (${type}), `
                }
            } )

            debugMessage += "\n"
        }
    }


    if ( !!location?.lat && !!location?.lng ) {
        if ( typeof location.lat === 'function' ) {
            newAddress.latLng = location.toJSON()
        } else {
            newAddress.latLng = location
        }
    }

    if ( debug ) {
        alert( debugMessage )
    }


    return newAddress;
}


/**
 * Leaves only addresses that match the specified types.
 * @param {array} addresses - Array of Google Maps geocoder response addresses.
 * @param {array} allowedTypes - Allowed address types.
 * @returns array
 */
export function filterAddressesByTypes(addresses = [], allowedTypes) {
    return addresses.filter( address => {
        if ( !allowedTypes )
            return [];

        return address.types.some( type => allowedTypes.includes( type ) );
    } )
}



/**
 * Checks whether the text contains coordinates.
 * @param {string} text - Comma separated coordinates.
 * @returns boolean
 */
export function hasCoordinates(text = '') {
    if ( !text || typeof text !== 'string' )
        return false;

    let [lat, lng] = text.split( ',' )
    lat = parseFloat( lat )
    lng = parseFloat( lng )

    return !Number.isNaN( lat ) && !Number.isNaN( lng );
}



/**
 * Retrieves the address from marker hints.
 * The function attaches a click handler to tooltip elements and, extracting address information from them, passes it on to the address setting function.
 * @param {object} events - Google map events object SEE: https://developers.google.com/maps/documentation/javascript/events
 * @param {node} container - Map conteiner element.
 * @param {object} currentAddress - Current address object.
 * @param {function} setAddress - Set address function.
 * @param {object} options - Plugin options.
 */
export function setAddressFromMarker(events, container, currentAddress, setAddress, options) {
    const infoWindow = container.querySelector( '.fma-info-suggestions' )

    if ( infoWindow ) {
        events.addDomListener( infoWindow, 'click', ( e ) => {
            if ( !!e.target.dataset?.content ) {
                e.preventDefault()

                const newAddress = getAddressFromComponents( JSON.parse( e.target.dataset.content ), JSON.parse( e.target.dataset.location ), options )

                /**
                 * To allow the user to clarify a location within an address, 
                 * coordinates will not be updated if the current location and the new location are within the same region.
                 */
                setAddress( {
                    address: newAddress,
                    updateCoordinates: newAddress.region_code !== currentAddress.region_code
                } )
            }
        } )
    }
}



export function SearchFieldComponent( {
    children,
    search, 
    setSearch,
    defaultSearch = '',
    suggestions, 
    setSuggestions,
    coordinates, 
    setCoordinates,
    services: {
        autocomplete,
        places,
        geocoder
    },
    place,
    setPlace,
    country_code
} ) {


    useEffect( () => {
        if ( !!autocomplete && !hasCoordinates( search ) && !Object.keys( coordinates ).length ) {
            if ( search.length >= 3 ) {
                autocomplete.getPlacePredictions( {
                    input: search,
                    types: ['address'],
                    componentRestrictions: {
                        country: country_code
                    }
                }, ( predictions, status ) => {       
                    if ( !!predictions?.length && status == 'OK' ) {
                        setSuggestions( predictions )
                    }
                } )
            } else {
                setSuggestions( [] )
                //setPlaceId( '' )

                if ( place && !!Object.keys( place )?.length ) {
                    setPlace( {} )
                }
            }
        }
    }, [
        search,
        autocomplete,
        place
    ] )


    useEffect( () => {
        if ( !!geocoder ) {
            if ( !!Object.keys( coordinates ).length ) {
                console.log('coo')
                geocoder.geocode( {
                    location: coordinates,
                    region: country_code,
                    //componentRestrictions: {
                        //country: country_code
                    //},
                    //result_type: 'administrative_area_level_1|administrative_area_level_2|administrative_area_level_3|administrative_area_level_4|administrative_area_level_5|sublocality|sublocality_level_1|sublocality_level_2|sublocality_level_3|sublocality_level_4|street_address|route',
                    //location_type: 'RANGE_INTERPOLATED'
                } )
                .then( ( response ) => {
                    if ( !!response?.results.length ) {
                        const filteredAddresses = filterAddressesByTypes( response.results, [
                            'street_address',
                            'route'
                        ] )
                        setSuggestions( filteredAddresses )

                        //console.log(response.results)
                    } else {
                        console.log( 'No results found' )
                    }
                } )
            } else {
                setSuggestions( [] )
            }
        }
    }, [
        coordinates,
        geocoder,
        //place
    ] )


    useEffect( () => {
        if ( place === null ) {
            setSearch( defaultSearch )
            setSuggestions( [] )
            setCoordinates( [] )
            //setPlaceId( '' )
        }
    }, [place] )



    return <>{ children }</>;
}



function createInfoSuggestions(suggestions) {

    let content = '<ul class="fma-info-suggestions">'

    suggestions.forEach( suggestion => {
        const addressComponents = JSON.stringify( suggestion.address_components )
        const location = JSON.stringify( suggestion.geometry.location.toJSON() )

        content += `<li><a href="#" class="fma-info-suggestion" data-content='${addressComponents}' data-location='${location}'>${suggestion.formatted_address}</a></li>`
    } )

    content += '</ul>'

    return content;
}


export function Marker( {
    position,
    map,
    title,
    draggable = false,
    getInfoContent,
    setAddress
} ) {
    const [marker, setMarker] = useState()
    const [infoWindow, setInfoWindow] = useState()
    const [open, setOpen] = useState( false )


    function openInfoWindow() {
        getInfoContent( marker.getPosition() )
        .then( response => {
            if ( !!response?.results.length ) {
                const filteredAddresses = filterAddressesByTypes( response.results, [
                    'street_address',
                    'route'
                ] )

                const content = createInfoSuggestions( filteredAddresses )

                infoWindow.setContent( content )
                infoWindow.open({
                    anchor: marker,
                    map,
                    shouldFocus: false
                } )
            } else {
                setOpen( false )
            }
        } )
        .catch( err => setOpen( false ) )
    }
    
    //console.log(position?.lat())
  
    useEffect(() => {
        if ( !marker ) {
            // create marker
            const newMarker = new google.maps.Marker( {
                draggable
            } )
            if ( draggable ) {
                newMarker.addListener( 'dragstart', () => setOpen( false ) )
                newMarker.addListener( 'dragend', () => setOpen( true ) )
            }
            setMarker( newMarker )

            // create info window
            if ( !infoWindow ) {
                const newInfoWindow = new google.maps.InfoWindow( {} )
                newInfoWindow.addListener( 'closeclick', () => setOpen( false ) )
                newInfoWindow.addListener( 'domready', () => setAddress() )

                setInfoWindow( newInfoWindow )
            }
        }
  

        return () => {
            if ( marker ) {
                marker.setMap( null )
            }
            if ( infoWindow ) {
                infoWindow.close()
            }
        };
    }, [
        marker,
        infoWindow
    ] )

    useEffect( () => {
        if ( marker ) {
            marker.setOptions( {
                position,
                title
            } )

            if ( !marker?.map ) {
                marker.setMap( map )
            }

            setOpen( false )
        }
    }, [marker, position] )


    useEffect( () => {
        if ( !!infoWindow ) {
            open ? openInfoWindow() : infoWindow.close()
        }
    }, [
        open,
        infoWindow
    ] )



    return null;
}


/**
 * 
 */
export function Map( {
    children,
    map,
    place,
    center: defaultCenter,
    zoom: defaultZoom,
    initZoom,
    hasAddress
} ) {
    const [zoom, setZoom] = useState( defaultZoom )
    const [center, setCenter] = useState( defaultCenter )


    useEffect( () => {
        map.setCenter( center )
        map.setZoom( zoom )
    }, [
        center, 
        zoom
    ] )


    useEffect( () => {
        if ( place !== null ) {
            const newCenter = !!place?.geometry?.location ? {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng()
            } : defaultCenter

            setCenter( newCenter )
            setZoom( defaultZoom )
        } else {
            setZoom( hasAddress ? defaultZoom : initZoom )
            setCenter( defaultCenter )
        }
    }, [place] )


    return (
        <>
            { children }
        </>
    )
}